// The simulated phone (comms.softphone with TELEPHONY_PROVIDER=simulator). The phone itself lives on the server
// (services/core/src/comms/provider/simulator.ts): each operator has an extension of their own, calls on it go through
// the same inbox and processor as RingCentral's, and the server's routes act on them and record every control.
// This engine only shows that phone and forwards the operator's actions. Nothing here is a real call, and it never
// says otherwise: the state shows `simulated`, and there is no audio.

import type { ControlAction, ControlArgs, EngineHost, PhoneCall, PhoneEngine } from './engine.ts';

interface ServerPhoneCall {
  sid: string;
  direction: 'inbound' | 'outbound';
  state: 'ringing' | 'connected' | 'held' | 'ended';
  remote: string;
  line: string;
  muted: boolean;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  consultFor: string | null;
  device?: 'softphone' | 'desk_phone' | null;
}

const toCall = (c: ServerPhoneCall): PhoneCall => ({
  id: c.sid,
  telephonySessionId: c.sid,
  direction: c.direction,
  state: c.state,
  remote: c.remote,
  line: c.line,
  muted: c.muted,
  startedAt: c.startedAt,
  answeredAt: c.answeredAt,
  endedAt: c.endedAt,
  endReason: c.endReason,
  consultFor: c.consultFor,
});

/** How long an ended call stays on the phone, so the operator sees how it ended. */
const ENDED_KEEP_MS = 8000;

export class SimulatorEngine implements PhoneEngine {
  readonly name = 'simulator' as const;
  readonly simulated = true;
  readonly recordsOwnControls = true;
  private list: PhoneCall[] = [];
  private subs = new Set<() => void>();
  private refreshing: Promise<void> | null = null;
  private again = false;
  private timers = new Set<ReturnType<typeof setTimeout>>();

  constructor(private readonly host: EngineHost, private readonly now: () => number = Date.now) {}

  calls(): PhoneCall[] {
    return this.list;
  }

  onChange(cb: () => void): void {
    this.subs.add(cb);
  }

  private changed() {
    for (const cb of this.subs) cb();
  }

  async start(): Promise<void> {
    await this.host.http('POST', '/api/softphone/sip');
    await this.refresh();
  }

  async stop(): Promise<void> {
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.list = [];
    this.changed();
  }

  recover(): Promise<void> {
    return this.refresh();
  }

  /**
   * Rereads the phone from the server. A call the server no longer lists has ended there: it is kept a few seconds as
   * ended (with what the server last said), never invented. One refresh at a time; a request during one runs after it.
   */
  refresh(): Promise<void> {
    if (this.refreshing) {
      this.again = true;
      return this.refreshing;
    }
    this.refreshing = (async () => {
      do {
        this.again = false;
        const r = await this.host.http<{ phone: ServerPhoneCall[] | null }>('GET', '/api/softphone/calls');
        // A call answered on the workstation's desk phone is not on this phone (the panel shows it from the server's
        // records, with the desk phone's controls).
        const desk = new Set((r.phone ?? []).filter((c) => c.device === 'desk_phone').map((c) => c.sid));
        const live = (r.phone ?? []).filter((c) => !desk.has(c.sid)).map(toCall);
        const ids = new Set(live.map((c) => c.id));
        const gone = this.list
          .filter((c) => !ids.has(c.id) && c.state !== 'ended')
          .map((c) => ({ ...c, state: 'ended' as const, endedAt: c.endedAt ?? new Date(this.now()).toISOString(), endReason: desk.has(c.id) ? 'AnsweredElsewhere' : c.endReason }));
        const keptEnded = this.list.filter((c) => c.state === 'ended' && !ids.has(c.id));
        this.list = [...live, ...keptEnded, ...gone];
        for (const c of gone) this.forgetLater(c.id);
      } while (this.again);
    })().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing.then(() => this.changed());
  }

  private forgetLater(id: string) {
    const t = setTimeout(() => {
      this.timers.delete(t);
      this.list = this.list.filter((c) => c.id !== id || c.state !== 'ended');
      this.changed();
    }, ENDED_KEEP_MS);
    this.timers.add(t);
  }

  async dial(to: string, callerId: string | null): Promise<PhoneCall | null> {
    const r = await this.host.http<{ call: ServerPhoneCall | null }>('POST', '/api/softphone/sim/dial', { to, ...(callerId ? { callerId } : {}) });
    await this.refresh();
    return r.call ? toCall(r.call) : null;
  }

  async control(callId: string, action: ControlAction, args: ControlArgs): Promise<{ consultCallId?: string }> {
    const body: Record<string, unknown> = {};
    if (args.to) body.to = args.to;
    if (args.digits) body.digits = args.digits;
    if (args.reason) body.reason = args.reason;
    try {
      const r = await this.host.http<{ consultSessionId?: string }>('POST', `/api/softphone/sim/calls/${encodeURIComponent(callId)}/${action}`, body);
      return r.consultSessionId ? { consultCallId: r.consultSessionId } : {};
    } finally {
      await this.refresh().catch(() => undefined);
    }
  }

  /**
   * The server says a call on this phone changed: reread (the message has no full number; the server's list has). A call
   * that ended takes its end time and reason from the message, since the server lists only live calls.
   */
  serverMessage(data: unknown): void {
    const d = data as { type?: string; call?: { sid?: string; state?: string; endedAt?: string | null; endReason?: string | null } } | null;
    if (d?.type !== 'call') return;
    const c = d.call;
    if (c?.state === 'ended' && c.sid) {
      const i = this.list.findIndex((x) => x.id === c.sid);
      if (i >= 0 && this.list[i]!.state !== 'ended') {
        this.list = this.list.map((x, j) => (j === i ? { ...x, state: 'ended', endedAt: c.endedAt ?? new Date(this.now()).toISOString(), endReason: c.endReason ?? null } : x));
        this.forgetLater(c.sid);
        this.changed();
      }
    }
    void this.refresh().catch(() => undefined);
  }
}
